import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { SurveyModel } from 'survey-core';

import { VotingInterface } from '@/components/VotingInterface';
import { SurveyDto } from '@/contracts/models/SurveyDto';
import { useTranslation } from '@/hooks/useTranslation';
import {
  LoadSurvey,
  LoadSurveySummary,
  SurveyPublicSummaryDto,
} from '@/services/feedbackApi';
import { loggingUtility } from '@/utils/loggingUtility';
import { getOrCreateSessionId } from '@/utils/sessionUtils';
import {
  extractBasicMetadata,
  extractSurveyMetadata,
} from '@/utils/trackingMetadataUtility';

import SurveyLandingSection from '../components/SurveyLandingSection';
import {
  AlreadyCompletedState,
  EmptyState,
  ErrorState,
  LoadingInsightsState,
  LoadingState,
} from '../components/SurveyLoadingStates';
import { SurveyRenderer } from '../components/SurveyRenderer';

import './TakeSurveyPage.css';

type PageView = 'landing' | 'survey' | 'completion';

interface TakeSurveyPageProps {
  skipLandingPage?: boolean;
}

const TakeSurveyPage: React.FC<TakeSurveyPageProps> = ({
  skipLandingPage = false,
}) => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { t } = useTranslation();

  // Page state
  const [currentView, setCurrentView] = useState<PageView>('landing');

  // Summary data (for landing page)
  const [summary, setSummary] = useState<SurveyPublicSummaryDto | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Full survey data (for taking)
  const [surveyData, setSurveyData] = useState<SurveyDto | null>(null);
  const [surveyLoading, setSurveyLoading] = useState(false);

  // Common state
  const [error, setError] = useState<string | null>(null);
  const [surveyAlreadyCompleted, setSurveyAlreadyCompleted] = useState(false);
  const [isSurveyCompleted, setIsSurveyCompleted] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [surveyResponseId, setSurveyResponseId] = useState<string | null>(null);
  const [userResponses, setUserResponses] = useState<Record<string, unknown>>(
    {}
  );
  const [collectedMetadata, setCollectedMetadata] = useState<Record<
    string,
    unknown
  > | null>(null);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create SurveyJS model from survey data
  const survey = useMemo(() => {
    if (!surveyData) return null;

    try {
      // Parse the surveyJson field from SurveyDto
      const surveyJson = JSON.parse(surveyData.surveyJson);

      // Configure survey for single question per page display with mobile support
      const configuredSurveyJson = {
        ...surveyJson,
        questionsOnPageMode: 'questionPerPage',
        showQuestionNumbers: 'on',
        showProgressBar: 'top',
        progressBarType: 'questions', // Show progress based on answered questions, not pages
        showPrevButton: false, // Remove Previous button - users can only move forward
      };

      const surveyModel = new SurveyModel(configuredSurveyJson);

      return surveyModel;
    } catch (error) {
      loggingUtility.error('Failed to parse survey JSON:', error);
      return null;
    }
  }, [surveyData]);

  // Load survey summary for landing page
  const loadSurveySummary = useCallback(async (id: string) => {
    setSummaryLoading(true);
    setError(null);

    try {
      const response = await LoadSurveySummary(id);
      setSummary(response.summary);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load survey';
      setError(errorMessage);
      loggingUtility.error('Survey summary loading error:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // Load full survey for taking
  const loadFullSurvey = useCallback(async (id: string) => {
    setSurveyLoading(true);
    setError(null);

    try {
      // Pass sessionId for anonymous user duplicate checking
      const sessionId = getOrCreateSessionId();
      const response = await LoadSurvey(id, sessionId);
      setSurveyData(response.survey);
      setSurveyAlreadyCompleted(response.alreadyCompleted);

      if (response.alreadyCompleted) {
        setCurrentView('completion');
        setIsSurveyCompleted(true);
      } else {
        setCurrentView('survey');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load survey';
      setError(errorMessage);
      loggingUtility.error('Survey loading error:', err);
    } finally {
      setSurveyLoading(false);
    }
  }, []);

  // Load summary on mount
  useEffect(() => {
    if (surveyId) {
      loadSurveySummary(surveyId);
    } else {
      setError('Survey ID is missing');
    }
  }, [surveyId, loadSurveySummary]);

  // Auto-start survey when accessed via /survey/:id/take route
  useEffect(() => {
    if (skipLandingPage && surveyId && summary && currentView === 'landing') {
      // Automatically start the survey (will trigger auth if needed)
      loadFullSurvey(surveyId);
    }
  }, [skipLandingPage, surveyId, summary, currentView, loadFullSurvey]);

  // Collect metadata in the background when survey loads
  useEffect(() => {
    if (surveyData && !collectedMetadata) {
      // Start collecting metadata immediately when survey loads
      const collectMetadata = async () => {
        try {
          // Get basic metadata immediately (synchronous)
          const basicMetadata = extractBasicMetadata();

          // Create a minimal survey model for geolocation collection
          const mockSurveyModel = {
            startTime: new Date(),
            endTime: null,
            timeSpent: 0,
            currentPageNo: 0,
            visiblePages: [],
            getAllQuestions: () => [],
          };

          // Get full metadata including geolocation (async)
          const fullMetadata = await extractSurveyMetadata(mockSurveyModel);

          // Merge basic and full metadata
          const metadata = { ...basicMetadata, ...fullMetadata };
          setCollectedMetadata(metadata);
        } catch {
          // Set basic metadata even if geolocation fails
          const basicMetadata = extractBasicMetadata();
          setCollectedMetadata(basicMetadata);
        }
      };

      collectMetadata();
    }
  }, [surveyData, collectedMetadata]);

  const handleSurveyComplete = useCallback(
    async (responses: Record<string, unknown>, responseId: string) => {
      setSurveyResponseId(responseId);
      setUserResponses(responses);

      const hasMixerQuestion = surveyData?.mixerQuestionId != null;
      if (hasMixerQuestion) {
        setLoadingInsights(true);
        loadingTimeoutRef.current = setTimeout(() => {
          setLoadingInsights(false);
          setIsSurveyCompleted(true);
          setCurrentView('completion');
        }, 500);
      } else {
        setIsSurveyCompleted(true);
        setCurrentView('completion');
      }
    },
    [surveyData?.mixerQuestionId]
  );

  const handleRetry = useCallback(() => {
    if (surveyId) {
      setCurrentView('landing');
      setSurveyData(null);
      setError(null);
      loadSurveySummary(surveyId);
    }
  }, [surveyId, loadSurveySummary]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Render loading state for summary
  if (summaryLoading && currentView === 'landing') {
    return <LoadingState />;
  }

  // Render error state
  if (error && currentView === 'landing') {
    return <ErrorState error={error} onRetry={handleRetry} />;
  }

  // Render empty state if no summary
  if (!summary && currentView === 'landing') {
    return <EmptyState />;
  }

  // If skipLandingPage is enabled, show loading instead of landing page
  // This prevents the brief flash of landing page before survey loads
  if (skipLandingPage && currentView === 'landing' && summary) {
    return <LoadingState />;
  }

  // Render landing page
  if (currentView === 'landing' && summary) {
    return <SurveyLandingSection summary={summary} />;
  }

  // Loading survey after clicking "Start Survey"
  if (surveyLoading) {
    return <LoadingState />;
  }

  // Render already-completed state (no retry button)
  if (surveyAlreadyCompleted && currentView === 'completion') {
    return <AlreadyCompletedState />;
  }

  // Render error state during survey taking
  if (error && currentView === 'survey') {
    return <ErrorState error={error} onRetry={handleRetry} />;
  }

  // Render empty state if no survey
  if (!survey && currentView === 'survey') {
    return <EmptyState />;
  }

  // Render loading insights state
  if (loadingInsights) {
    return <LoadingInsightsState />;
  }

  // Render completion card + voting interface when survey is complete
  if (isSurveyCompleted && currentView === 'completion') {
    const hasMixerQuestion = surveyData?.mixerQuestionId != null;
    const surveyCompletionMessage = survey?.completedHtml;

    return (
      <main className='min-h-screen bg-white py-8'>
        <div className='max-w-5xl mx-auto px-8'>
          <div className='w-full rounded-lg px-10 pt-6 pb-7 bg-gray-100 shadow-md'>
            {/* Completion header in outer gray container */}
            <div className='mb-5'>
              {/* Success notification */}
              <div className='mb-6'>
                <div className='mb-4'>
                  <svg
                    className='w-16 h-16 mx-auto text-green-500'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                    role='img'
                    aria-label={t('survey.completionSuccessIcon')}
                  >
                    <title>{t('survey.completionSuccessIcon')}</title>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                </div>
                <h2 className='text-2xl font-semibold text-gray-900 mb-2 text-center'>
                  {t('survey.completed')}
                </h2>
                <p className='text-gray-600 text-center'>
                  {surveyCompletionMessage || t('survey.completionMessage')}
                </p>
              </div>

              {/* Community Mixer context - show when mixer question exists */}
              {hasMixerQuestion && (
                <>
                  <h1 className='text-2xl font-bold mb-6 text-gray-900'>
                    {t('voting.communityMixer')}
                  </h1>

                  <p className='text-gray-700 text-base leading-relaxed'>
                    {t('voting.communityMixerDescription')}
                  </p>
                </>
              )}
            </div>

            {/* Voting interface in white inner card - show if mixer question exists */}
            {hasMixerQuestion && surveyData && surveyResponseId && (
              <div className='bg-white rounded-lg p-10 shadow-sm'>
                <VotingInterface
                  surveyId={surveyId!}
                  responseId={surveyResponseId}
                  userResponses={userResponses}
                  mode='embedded'
                  showHeader={false}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  // Render survey
  if (currentView === 'survey' && survey) {
    return (
      <SurveyRenderer
        survey={survey}
        surveyId={surveyId!}
        collectedMetadata={collectedMetadata}
        onComplete={handleSurveyComplete}
      />
    );
  }

  // Fallback
  return <EmptyState />;
};

export default TakeSurveyPage;
